// covered-by: packages/schemas/src/christo/__tests__/versionComparison.test.ts
import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi/dist/zod-extensions";
import { z } from "zod";

extendZodWithOpenApi(z);

/**
 * A comparison of two artwork versions, window by window, as a client draws
 * it: the versions side by side, a list of numbered changes, the text edits of
 * each. warhol makes it (`contracts/comparison.py`, spec dave.savostyanov/0081)
 * and christo camelCases it and turns each image key into a link on the media
 * host at its border (`warholComparisonsForChat`).
 */

/** One PNG of the comparison, on the product's media host. */
export const askPunttComparisonImageSchema = z.object({
  key: z.string().min(1),
  source: z.string().url(),
  name: z.string().min(1),
});
export type AskPunttComparisonImage = z.infer<
  typeof askPunttComparisonImageSchema
>;

/** A rectangle in fractions of what it sits on: a page, or a cut-out. */
export const askPunttComparisonRectSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  // why the openapi override: gt(0) renders a boolean `exclusiveMinimum`, which
  // the draft-07 validator in christo's jest.setup rejects for every route.
  width: z.number().gt(0).max(1).openapi({ exclusiveMinimum: 0 }),
  height: z.number().gt(0).max(1).openapi({ exclusiveMinimum: 0 }),
});

/**
 * One edit to the printed words inside a window. `moved`: both versions print
 * the words, at another place on the page -- never a removal.
 */
export const askPunttComparisonChangeSchema = z.object({
  kind: z.enum(["replaced", "added", "removed", "moved"]),
  before: z.string(),
  after: z.string(),
  near: z.string().default(""),
});
export type AskPunttComparisonChange = z.infer<
  typeof askPunttComparisonChangeSchema
>;

/** One changed window of the newer version, under the number the reply calls it. */
export const askPunttComparisonRegionSchema = z.object({
  number: z.number().int().min(1),
  page: z.number().int().min(1),
  /** The window on that page of the newer version, in fractions of the page. */
  box: askPunttComparisonRectSchema,
  kind: z.enum(["content", "geometry", "added"]),
  /** `text`: words changed, see `changes`. `pixels`: the picture differs. `moved`: the same words, elsewhere. */
  evidence: z.enum(["text", "pixels", "moved"]),
  readByOcr: z.boolean().default(false),
  foundByOcr: z.boolean().default(false),
  changes: z.array(askPunttComparisonChangeSchema).default([]),
  /** Text edits in this window past the ones listed. */
  changesMore: z.number().int().min(0).default(0),
  /** The window cut from the older / newer version, differences tinted; absent when not drawn. */
  before: askPunttComparisonImageSchema.nullable().default(null),
  after: askPunttComparisonImageSchema.nullable().default(null),
  /** Where the window sits inside either cut-out, in fractions of the cut-out. */
  window: askPunttComparisonRectSchema.nullable().default(null),
});
export type AskPunttComparisonRegion = z.infer<
  typeof askPunttComparisonRegionSchema
>;

/** One page of the newer version with its windows boxed and numbered. */
export const askPunttComparisonPageSchema = z.object({
  page: z.number().int().min(1),
  overview: askPunttComparisonImageSchema,
});

export const askPunttVersionComparisonSchema = z.object({
  /** Names this pair within the chat; stable across reads. */
  id: z.string().min(1),
  before: z.string().min(1),
  after: z.string().min(1),
  /** `partial`: some pages or windows were not compared. */
  status: z.enum(["changed", "partial"]),
  pages: z.array(askPunttComparisonPageSchema).default([]),
  regions: z.array(askPunttComparisonRegionSchema).default([]),
  regionsTruncated: z.boolean().default(false),
  textChanges: z.number().int().min(0).default(0),
  visualChanges: z.number().int().min(0).default(0),
  /** Every window on one tall picture: what a client without this view shows. */
  sheet: askPunttComparisonImageSchema.nullable().default(null),
});
export type AskPunttVersionComparison = z.infer<
  typeof askPunttVersionComparisonSchema
>;
